// Contenido del informe y revisión automática antes de firmar.
// Compartido por la estación de informe (UploadDocuments.tsx, platform/ui-next)
// y el panel lateral (PanelRisReport.tsx, extensions/default): el script 10
// copia este mismo archivo junto a cada uno. Editar aquí, nunca en una sola copia.

// ─── Contenido del informe ───────────────────────────────────────────────────
export const SECCIONES = [
  { id: 'tecnica', titulo: 'Técnica', ayuda: 'Equipo, protocolo, contraste, reconstrucciones…' },
  { id: 'hallazgos', titulo: 'Hallazgos', ayuda: 'Descripción de lo observado, por órganos o regiones…' },
  // El hospital titula esta sección "CONCLUSIONES" (plantilla Word del servicio)
  { id: 'impresion', titulo: 'Conclusiones', ayuda: 'Impresión diagnóstica del estudio…' },
  { id: 'recomendaciones', titulo: 'Recomendaciones', ayuda: 'Opcional: controles, estudios complementarios…' },
];

// Pone primero las plantillas que nombran la región del estudio: "TC Abdomen y
// pelvis" para "ABDOMINOPELVICO C/C". Se compara por raíz de 5 letras porque las
// descripciones de los equipos vienen abreviadas o juntas.
export const aPlano = s => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
// Cuántas palabras del nombre de la plantilla aparecen en la descripción del estudio
export const coincideConEstudio = (t, descripcion) => {
  const desc = aPlano(descripcion);
  if (!desc) return 0;
  return aPlano(t.name)
    .split(/[^a-z]+/)
    .filter(p => p.length >= 5 && desc.includes(p.slice(0, 5))).length;
};
export const ordenarPorEstudio = (lista, descripcion) => {
  const desc = aPlano(descripcion);
  if (!desc) return lista;
  const puntaje = t =>
    aPlano(t.name)
      .split(/[^a-z]+/)
      .filter(p => p.length >= 5)
      .filter(p => desc.includes(p.slice(0, 5))).length;
  return lista
    .map((t, i) => ({ t, i, p: puntaje(t) }))
    .sort((a, b) => b.p - a.p || a.i - b.i)
    .map(x => x.t);
};

// ─── Revisión antes de firmar ────────────────────────────────────────────────
// Reglas fijas sobre el texto, sin servicios externos. Solo avisa: el médico
// decide si corrige o firma igual. Tipos: 'error' (campo sin completar o dato
// que no corresponde al paciente), 'aviso' (posible incoherencia), 'critico'
// (hallazgo urgente afirmado), 'guia' (recordatorio de guía de seguimiento).
export const REGIONES = {
  cráneo: { estudio: ['crane', 'cerebr', 'encef', 'cabez'], texto: ['crane', 'cerebr', 'encef', 'hemisferi', 'ventricul', 'fosa posterior', 'tallo cerebral'] },
  tórax: { estudio: ['torax', 'tora', 'pulmo', 'pecho'], texto: ['pulmon', 'mediastin', 'pleura', 'traqueobronq', 'torax'] },
  abdomen: { estudio: ['abdom', 'higad', 'hepat', 'renal', 'rinon', 'urotom'], texto: ['higado', 'hepat', 'vesicula biliar', 'pancrea', 'bazo', 'rinon', 'abdom'] },
  columna: { estudio: ['colum', 'lumba', 'cervic', 'dorsal', 'sacro'], texto: ['vertebra', 'intervertebral', 'canal raquideo', 'lordosis', 'facetari'] },
  senos: { estudio: ['seno', 'paranas', 'maxilar'], texto: ['senos maxilares', 'etmoidal', 'osteomeatal', 'cornete'] },
};
// [raíz sin tildes, cómo se muestra]
export const ORGANOS_FEMENINOS = [['utero', 'útero'], ['ovario', 'ovario'], ['endometri', 'endometrio'], ['gestacion', 'gestación'], ['embrion', 'embrión'], ['embarazo', 'embarazo']];
export const ORGANOS_MASCULINOS = [['prostat', 'próstata'], ['testicul', 'testículo'], ['escroto', 'escroto'], ['vesiculas seminales', 'vesículas seminales']];
export const CON_LADO = [
  ['rinon', 'riñón'], ['pulmon', 'pulmón'], ['hemitorax', 'hemitórax'], ['neumotorax', 'neumotórax'], ['derrame pleural', 'derrame pleural'],
  ['ovario', 'ovario'], ['mama', 'mama'], ['hemisferio', 'hemisferio'], ['seno maxilar', 'seno maxilar'], ['rodilla', 'rodilla'],
  ['hombro', 'hombro'], ['cadera', 'cadera'], ['tobillo', 'tobillo'], ['codo', 'codo'], ['muneca', 'muñeca'], ['testiculo', 'testículo'],
  ['ureter', 'uréter'], ['femur', 'fémur'], ['tibia', 'tibia'], ['humero', 'húmero'], ['clavicula', 'clavícula'],
];
export const CRITICOS = [
  ['hemorragia intracraneal', /hemorragia (intra|sub|epi|parenquimatosa|intraparenquimatosa)|hematoma (sub|epi)dural|hemorragia subaracnoidea/],
  ['neumotórax', /neumotorax/],
  ['tromboembolismo pulmonar', /tromboembolismo|\btep\b|embolia pulmonar/],
  ['disección aórtica', /diseccion (de la )?aort/],
  ['neumoperitoneo', /neumoperitoneo|aire libre (en|intra)/],
  ['desplazamiento de la línea media', /desplazamiento de (la )?linea media/],
  ['herniación cerebral', /herniacion (sub|trans|uncal|amigdal)/],
  ['obstrucción intestinal', /obstruccion intestinal|oclusion intestinal/],
  ['embarazo ectópico', /ectopico/],
  ['torsión', /torsion (testicular|ovarica|anexial)/],
];
export const NEGACION = /\b(no|sin|ni|descart\w*|ausencia de|negativ\w*|libre de)\b/;

export const normal = s => aPlano(s).replace(/\s+/g, ' ');
// Cada párrafo y cada frase por separado (las negaciones valen dentro de la frase)
export const oraciones = html =>
  aPlano(
    (html || '')
      .replace(/<\/(p|div|li|h\d)>|<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
  )
    .split(/\n|[.;]\s+/)
    .map(o => o.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

// El término está afirmado si en su oración no hay una negación antes
export const afirmado = (oracion, re) => {
  const m = oracion.match(re);
  if (!m) return false;
  return !NEGACION.test(oracion.slice(0, m.index));
};

export function revisarInforme(secciones, d) {
  const obs = [];
  const agregar = (tipo, seccion, texto) => obs.push({ tipo, seccion, texto });
  const porSeccion = Object.fromEntries(SECCIONES.map(s => [s.id, oraciones(secciones[s.id])]));
  const todo = Object.values(porSeccion).flat();
  const textoTodo = todo.join(' ');
  const titulo = id => SECCIONES.find(s => s.id === id)?.titulo || id;

  // 0. Sin conclusiones (el panel lateral no las separa al escribir)
  if (!textoPlano(secciones.impresion || '')) agregar('error', 'impresion', 'Faltan las CONCLUSIONES del informe.');

  // 1. Campos de plantilla sin completar
  SECCIONES.forEach(s => {
    const t = textoPlano(secciones[s.id] || '');
    if (/_{2,}|\[[^\]]{2,}\]/.test(t)) agregar('error', s.id, `${titulo(s.id)}: quedan campos de la plantilla sin completar (___ o [ … ]).`);
  });

  // 2. El informe describe otra región que la del estudio (plantilla equivocada)
  const desc = normal(d.estudio);
  const delEstudio = Object.keys(REGIONES).filter(r => REGIONES[r].estudio.some(k => desc.includes(k)));
  if (delEstudio.length) {
    const cuerpo = normal([secciones.tecnica, secciones.hallazgos].map(textoPlano).join(' '));
    const menciones = r => REGIONES[r].texto.filter(k => cuerpo.includes(k)).length;
    const ajena = Object.keys(REGIONES).find(r => !delEstudio.includes(r) && menciones(r) >= 2);
    if (ajena && delEstudio.every(r => menciones(r) === 0)) {
      agregar('error', 'tecnica', `El estudio es "${d.estudio}" pero el informe describe ${ajena}. ¿Se usó la plantilla equivocada?`);
    }
  }

  // 3. Otra modalidad en la técnica
  const tec = normal(textoPlano(secciones.tecnica || ''));
  const mod = d.modalidades || [];
  if (mod.includes('CT') && /ecograf|radiograf|resonancia/.test(tec) && !/tomograf/.test(tec))
    agregar('aviso', 'tecnica', 'El estudio es una tomografía pero la técnica describe otra modalidad.');
  if (mod.includes('US') && /tomograf|radiograf/.test(tec) && !/ecograf/.test(tec))
    agregar('aviso', 'tecnica', 'El estudio es una ecografía pero la técnica describe otra modalidad.');

  // 4. Órganos que no corresponden al sexo del paciente
  if (d.sexo === 'Masculino') {
    const o = ORGANOS_FEMENINOS.find(([k]) => todo.some(x => afirmado(x, new RegExp(k))));
    if (o) agregar('error', 'hallazgos', `El paciente es masculino y el informe menciona "${o[1]}".`);
  }
  if (d.sexo === 'Femenino') {
    const o = ORGANOS_MASCULINOS.find(([k]) => todo.some(x => afirmado(x, new RegExp(k))));
    if (o) agregar('error', 'hallazgos', `La paciente es femenina y el informe menciona "${o[1]}".`);
  }

  // 5. Lateralidad: la conclusión nombra un lado que los hallazgos no mencionan para ese órgano
  const lados = (lista, org) => {
    const l = new Set();
    lista.filter(o => o.includes(org)).forEach(o => {
      if (/derech/.test(o)) l.add('derecho');
      if (/izquierd/.test(o)) l.add('izquierdo');
    });
    return l;
  };
  CON_LADO.forEach(([org, nombre]) => {
    const enConcl = lados(porSeccion.impresion, org);
    const enHall = lados(porSeccion.hallazgos, org);
    enConcl.forEach(lado => {
      if (enHall.size && !enHall.has(lado))
        agregar('aviso', 'impresion', `Lateralidad: las conclusiones dicen ${nombre} ${lado}, pero en los hallazgos aparece solo el lado ${[...enHall].join(' y ')}.`);
    });
  });

  // 6. Hallazgos críticos afirmados (no negados)
  const criticos = CRITICOS.filter(([, re]) => todo.some(x => afirmado(x, re))).map(([nombre]) => nombre);
  criticos.forEach(nombre =>
    agregar('critico', 'hallazgos', `Hallazgo crítico: ${nombre}. Considere comunicarlo de inmediato al médico tratante y dejar constancia.`)
  );
  // Conclusión "normal" con un hallazgo crítico en el informe (típico al partir de una plantilla normal)
  const concl = porSeccion.impresion.join(' ');
  if (criticos.length && /sin alteraciones|caracteristicas normales|dentro de limites normales|sin hallazgos/.test(concl) && !criticos.some(n => concl.includes(aPlano(n).split(' ')[0])))
    agregar('error', 'impresion', `Las conclusiones dicen que el estudio es normal, pero hay un hallazgo crítico (${criticos.join(', ')}).`);

  // 7. Guías de seguimiento
  if (todo.some(o => afirmado(o, /nodulo/) && /pulmon|lobulo|parenquima pulmonar/.test(o)) && !/fleischner|lung-?rads/.test(textoTodo))
    agregar('guia', 'recomendaciones', 'Nódulo pulmonar: considere indicar el seguimiento según las guías de Fleischner (2017) o Lung-RADS si es tamizaje.');
  if (todo.some(o => afirmado(o, /nodulo/) && /tiroid/.test(o)) && !/ti-?rads/.test(textoTodo))
    agregar('guia', 'impresion', 'Nódulo tiroideo: falta la categoría ACR TI-RADS.');
  if (/\bmama/.test(textoTodo) && (mod.includes('MG') || mod.includes('US')) && !/bi-?rads/.test(textoTodo))
    agregar('guia', 'impresion', 'Estudio de mama: falta la categoría BI-RADS.');
  if (todo.some(o => afirmado(o, /quiste/) && /renal|rinon/.test(o) && /(complej|tabique|septo|realce|calcific)/.test(o)) && !/bosniak/.test(textoTodo))
    agregar('guia', 'impresion', 'Quiste renal complejo: considere clasificarlo según Bosniak (2019).');
  if (todo.some(o => afirmado(o, /(nodulo|masa|lesion|adenoma)/) && /suprarrenal|adrenal/.test(o)))
    agregar('guia', 'recomendaciones', 'Lesión suprarrenal incidental: considere las recomendaciones del ACR para incidentalomas suprarrenales.');

  // 8. Palabras repetidas (típico del dictado: "de de", "la la")
  SECCIONES.forEach(s => {
    const m = normal(textoPlano(secciones[s.id] || '')).match(/\b([a-z]{2,})\s+\1\b/);
    if (m) agregar('aviso', s.id, `${titulo(s.id)}: palabra repetida "${m[1]} ${m[1]}".`);
  });

  const orden = { critico: 0, error: 1, aviso: 2, guia: 3 };
  return obs.sort((a, b) => orden[a.tipo] - orden[b.tipo]);
}

export const seccionDeTitulo = titulo => {
  const t = titulo.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
  if (/TECNICA|PROTOCOLO/.test(t)) return 'tecnica';
  if (/IMPRESION|CONCLUSION|DIAGNOSTIC/.test(t)) return 'impresion';
  if (/RECOMENDA|SUGERENCIA/.test(t)) return 'recomendaciones';
  if (/HALLAZGO|DESCRIPCION|INFORME/.test(t)) return 'hallazgos';
  return null;
};

export const escapar = s =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const ETIQUETAS = ['P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'H3', 'H4', 'UL', 'OL', 'LI', 'DIV', 'SPAN'];

export function sanitizar(html) {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const limpiar = nodo => {
    [...nodo.childNodes].forEach(hijo => {
      if (hijo.nodeType === Node.TEXT_NODE) return;
      if (hijo.nodeType !== Node.ELEMENT_NODE) return hijo.remove();
      if (!ETIQUETAS.includes(hijo.tagName)) {
        if (['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT'].includes(hijo.tagName)) return hijo.remove();
        limpiar(hijo);
        hijo.replaceWith(...hijo.childNodes);
        return;
      }
      [...hijo.attributes].forEach(a => hijo.removeAttribute(a.name));
      limpiar(hijo);
    });
  };
  const raiz = doc.body.firstChild;
  limpiar(raiz);
  return raiz.innerHTML;
}

export const esHtml = s => /<\/?(p|h[1-6]|ul|ol|li|br|strong|b|em|i|u|div)\b/i.test(s);
export const textoPlano = html => html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();

// Texto viejo ("HALLAZGOS:\n...") o HTML guardado -> { tecnica, hallazgos, ... }
export function separarSecciones(contenido) {
  const vacio = { tecnica: '', hallazgos: '', impresion: '', recomendaciones: '' };
  if (!contenido) return vacio;
  let html = contenido;
  if (!esHtml(contenido)) {
    html = contenido
      .split(/\n/)
      .map(l => l.trim())
      .filter(Boolean)
      .map(linea => {
        const s = linea.match(/^([A-ZÁÉÍÓÚÑÜ][A-ZÁÉÍÓÚÑÜ0-9 /().-]{2,}):\s*(.*)$/);
        if (s) return `<h4>${escapar(s[1])}</h4>${s[2] ? `<p>${escapar(s[2])}</p>` : ''}`;
        return `<p>${escapar(linea)}</p>`;
      })
      .join('');
  }
  const doc = new DOMParser().parseFromString(`<div>${sanitizar(html)}</div>`, 'text/html');
  let actual = 'hallazgos';
  [...doc.body.firstChild.childNodes].forEach(nodo => {
    const tag = nodo.nodeName;
    if (tag === 'H3' || tag === 'H4') {
      const id = seccionDeTitulo(nodo.textContent || '');
      if (id) {
        actual = id;
        return;
      }
      // Sección que no es de las cuatro: queda como subtítulo dentro de la actual
      vacio[actual] += `<p><b>${escapar(nodo.textContent)}</b></p>`;
      return;
    }
    vacio[actual] += nodo.nodeType === Node.TEXT_NODE ? `<p>${escapar(nodo.textContent)}</p>` : nodo.outerHTML;
  });
  return vacio;
}

export const unirSecciones = secciones =>
  SECCIONES.filter(s => textoPlano(secciones[s.id] || ''))
    .map(s => `<h4>${s.titulo.toUpperCase()}</h4>${sanitizar(secciones[s.id])}`)
    .join('');
